import {addressWithoutPOBox, phoneValidation} from '@/components/Helpers/validationHelpers.ts';
import type {TFunction} from 'i18next';
import {z} from 'zod';

export const form203Schema = (t: TFunction<'translation', undefined>) =>
    z.object({
        guardians: z
            .array(
                z
                    .object({
                        id: z.string().uuid(),
                        firstName: z
                            .string({message: `${t('sef203GuardianFirst')} is required.`})
                            .min(1, `${t('sef203GuardianFirst')} is required.`),
                        lastName: z
                            .string({message: `${t('sef203GuardianLast')} is required.`})
                            .min(1, `${t('sef203GuardianFirst')} is required.`),
                        relationship: z.string({
                            message: `${t('sef203GuardianRelationship')} is required.`,
                        }),
                        relationshipOther: z.string().nullish(),
                        emailLanguage: z.string({message: `${t('requiredErrorFull')}`}),
                        highestEducation: z.string({
                            message: `${t('sef203GuardianHighestEducation')} is required.`,
                        }),
                        address: addressWithoutPOBox(
                            `${t('sef203GuardianAddress')} is required.`,
                        ).nullish(),
                        city: z
                            .string({message: `${t('sef203GuardianCity')} is required`})
                            .nullish(),
                        state: z
                            .string({message: `${t('sef203GuardianState')} is required`})
                            .min(1, `${t('sef203GuardianState')} is required`),
                        zip: z.string({message: `${t('sef203GuardianZip')} is required`}).nullish(),
                        phone1: phoneValidation(t('phoneValidation')),
                        phone1Type: z.string({
                            message: `${t('sef203GuardianPhoneType')} is required.`,
                        }),
                        phone2: phoneValidation(t('phoneValidation'), true).nullish(),
                        phone2Type: z.string().nullish(),
                        email: z.string(),
                        contactDetails: z
                            .array(z.string(), {
                                message: `${t('sef203GuardianContact')} is required.`,
                            })
                            .min(1, {message: `${t('sef203GuardianContact')} is required.`}),
                        contactOrder: z.coerce.number(),
                    })
                    .refine(
                        guardian =>
                            guardian.relationship !== 'Other' ||
                            (guardian.relationship === 'Other' && guardian.relationshipOther),
                        {
                            path: ['relationshipOther'],
                            message:
                                'You must enter Relationship Other when Relationship is Other.',
                        },
                    )
                    .refine(
                        guardian => {
                            if (!guardian.phone2) {
                                return true;
                            }

                            return guardian.phone2Type;
                        },
                        {
                            path: ['phone2Type'],
                            message: `${t('sef203GuardianPhoneType')} is required.`,
                        },
                    )
                    .refine(
                        guardian => {
                            if (
                                guardian.contactDetails?.length !== 1 ||
                                !guardian.contactDetails.includes(t('sef203GuardianContactOp5'))
                            ) {
                                return guardian.address;
                            }
                            return true;
                        },
                        {path: ['address'], message: `${t('sef203GuardianAddress')} is required.`},
                    )
                    .refine(
                        guardian => {
                            if (
                                guardian.contactDetails?.length !== 1 ||
                                !guardian.contactDetails.includes(t('sef203GuardianContactOp5'))
                            ) {
                                return guardian.city;
                            }
                            return true;
                        },
                        {path: ['city'], message: `${t('sef203GuardianCity')} is required.`},
                    )
                    .refine(
                        guardian => {
                            if (
                                guardian.contactDetails?.length !== 1 ||
                                !guardian.contactDetails.includes(t('sef203GuardianContactOp5'))
                            ) {
                                return guardian.zip;
                            }
                            return true;
                        },
                        {path: ['zip'], message: `${t('sef203GuardianZip')} is required.`},
                    )
                    .superRefine((guardian, ctx) => {
                        if (
                            guardian.contactDetails?.length !== 1 ||
                            !guardian.contactDetails.includes(t('sef203GuardianContactOp5'))
                        ) {
                            const emailResult = z.string().email().safeParse(guardian.email);
                            if (!emailResult.success) {
                                return ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    path: ['email'],
                                    message: `${t('sef203GuardianEmail')} must be valid.`,
                                });
                            }
                        }
                        return true;
                    }),
            )
            .min(2, t('requiredContacts')),
    });

export const form203Refine = (
    schema: ReturnType<typeof form203Schema>,
    t: TFunction<'translation', undefined>,
) => {
    return schema.superRefine((data, ctx) => {
        // Each guardian must have at least one phone number that no other guardian
        // lists. Two (or more) guardians may share a number, as long as each of them
        // still has another number that is unique to them. This scales to any number
        // of guardians: every guardian needs one phone that nobody else uses.
        const guardianCountByPhone = new Map<string, number>();

        for (const guardian of data.guardians) {
            // A guardian counts once per distinct number, even if both of their
            // numbers happen to be the same.
            const distinctPhones = new Set(
                [guardian.phone1, guardian.phone2].filter(
                    (phone): phone is string => Boolean(phone),
                ),
            );

            for (const phone of distinctPhones) {
                guardianCountByPhone.set(phone, (guardianCountByPhone.get(phone) ?? 0) + 1);
            }
        }

        data.guardians.forEach((guardian, index) => {
            const phones = [guardian.phone1, guardian.phone2].filter(
                (phone): phone is string => Boolean(phone),
            );

            const hasUniquePhone = phones.some(phone => guardianCountByPhone.get(phone) === 1);

            if (!hasUniquePhone) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['guardians', index, 'phone1'],
                    message: t('phoneCannotMatch'),
                });
            }
        });
    });
};
